<template>
	<section class="section">
		<h4 class="title">Сумма доставки</h4>

		<ul class="cargo-detail">
			<li v-for="cargo in cargo_details" class="cargo-detail__item">
				<span>Груз № {{ cargo.number }}</span>
				<span class="cargo-detail__size">{{ cargo.size }} </span>
				<span>{{ cargo.price }} ¥</span>
			</li>

			<li v-if="isInsure" class="cargo-detail__item insurance-row">
				<span>Страхование груза</span>
				<span>{{ formatNumber(totalRes?.ensurance_cost ?? 0) }} ¥</span>
			</li>
		</ul>

		<b class="total row-val">
			<span>Итого</span>
			<span class="price">{{ totals.totalCny }} ¥</span>
		</b>

		<p class="total-rub row-val">
			<span>По курсу ЦБ на {{ today }}</span>
			<span class="value">~ {{ totals.totalRub }} ₽</span>
		</p>

		<UIShInputFile class="upload-files" v-model="files" :max="5" multiple />

		<div class="insurance">
			<span>Страхование груза</span>
			<UIShSwitch v-model="isInsure" />
		</div>

		<div class="footer">
			<UIShButton
				label="Получить расчет"
				md
				:xl="1920"
				@click="$emit('submit')"
			/>

			<UIShLink to="/#delivery-options">
				<UIShButtonLabel sm :xl="1920">
					<span>{{ 'Альтернативные\nварианты доставки' }}</span>
				</UIShButtonLabel>
			</UIShLink>
		</div>
	</section>
</template>

<script setup lang="ts">
import { formatDate, formatNumber, getWordByNumber } from '@/utils';
import type {
	ContainerData,
	ContainerBlocks,
	CalcPayload,
	CargoGoods,
	UploadFileType,
	ApiDeliveriesResult
} from '@/common/types';

const $emit = defineEmits<{
	(e: 'submit'): void;
	(e: 'result', d: ApiDeliveriesResult): void;
}>();
const props = defineProps<{
	isClearance: boolean;
	containers: ContainerData[];
	containersCargoData: ContainerBlocks[];
}>();

const isInsure = defineModel<boolean>('isInsure', { required: true });
const files = defineModel<UploadFileType[]>('files', { default: [] });

const today = formatDate(new Date());

const { postCalcPrice, totalRes, autoRes, aviaRes } = useCalcTotal();
const calcStorage = useCalculatorStorage();

const totals = computed(() => {
	return {
		weight: formatNumber(totalRes.value?.summary_weight ?? 0),
		weightPrice: formatNumber(totalRes.value?.summary_weight_cost ?? 0),
		totalCny: formatNumber(totalRes.value?.result_cny ?? 0),
		totalRub: formatNumber(totalRes.value?.result_rub ?? 0),
		ensurance: formatNumber(totalRes.value?.ensurance_cost ?? 0)
	};
});

const cargos = computed<CalcPayload['cargos']>(() => {
	return props.containers.map((c) => {
		return {
			height: c.height ?? 0,
			width: c.width ?? 0,
			length: c.length ?? 0,
			weight: c.weight ?? 0,
			quantity: c.count ?? 0,
			goods: getContainerGoods(c._meta.id)
		};
	});
});

const cargo_details = computed(() => {
	return props.containers
		.map((c, idx) => {
			const details = totalRes.value?.cargos_detailed[idx];
			const length = c.length ?? 0;
			const width = c.width ?? 0;
			const height = c.height ?? 0;
			const weight = c.weight ?? 0;
			const count = c.count ?? 0;

			const totalVolume = formatNumber(
				((length * width * height) / 1000000000) * count
			);

			const totalWeight = weight * count;
			const totalWord = getWordByNumber(count, [
				'место',
				'места',
				'мест'
			]);

			return {
				length,
				width,
				height,
				weight,
				count,
				number: idx + 1,
				price: details?.price_cny ? formatNumber(details.price_cny) : 0,
				size: `объем ${totalVolume} м³,\nвес ${formatNumber(totalWeight)} кг, ${count} ${totalWord}`
			};
		})
		.filter((c) => {
			return ![c.length, c.width, c.height, c.weight, c.count].some(
				(v) => v <= 0
			);
		});
});

const handleCalc = useDebounce(postCalcPrice, 500);

watchEffect(() => {
	$emit('result', {
		avia: aviaRes.value,
		auto: autoRes.value,
		train: totalRes.value
	});
});

watch([cargos, isInsure], () => {
	calcStorage.saveValues(isInsure.value, props.containers);

	handleCalc({
		type: 'train',
		isInsure: isInsure.value,
		isClearance: props.isClearance,
		cargos: cargos.value
	});
});

function getContainerGoods(id: string): CargoGoods[] {
	const targetCargoData = props.containersCargoData.find((d) => d.id === id);
	const validBlocks = targetCargoData?.blocks.filter((b) => b.category) ?? [];

	return validBlocks.map((v) => ({
		name: v.name,
		price: v.price,
		category: v.category,
		currency: v.currency
	}));
}
</script>

<style lang="scss" scoped>
.section {
	padding: 0.889rem;
	border-radius: 1.111rem;
	color: var(--color-gray950);
	background-color: var(--color-white);
}

.title {
	@include useFont(tlgs);
}

.row-val {
	display: flex;
	gap: 0.556rem;
	align-items: center;
	justify-content: space-between;
}

.cargo-detail {
	display: flex;
	flex-direction: column;
	margin-top: 0.889rem;
	gap: 0.28rem;
	@include useFont(tsms);

	&__item {
		display: grid;
		grid-template-columns: 4.2rem 1fr auto;
		grid-template-rows: auto;
		column-gap: 0.2rem;

		&.insurance-row {
			grid-template-columns: auto auto;
			justify-content: space-between;
		}
	}

	&__size {
		white-space: pre-line;
		color: var(--color-gray500);
	}
}

.total {
	margin-top: 0.889rem;
	padding-top: 0.556rem;
	@include useFont(tlgs);
	border-top: 0.056rem solid var(--color-gray200);

	.price {
		color: var(--color-brand500);
	}
}

.total-rub {
	margin-top: 0.222rem;
	@include useFont(tsms);
	color: var(--color-gray500);

	.value {
		color: var(--color-gray950);
	}
}

.upload-files {
	margin-top: 2.222rem;
}

.insurance {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: nowrap;
	margin-top: 0.889rem;
	border-radius: 0.889rem;
	padding: 0.889rem;
	gap: 0.667rem;
	background-color: var(--color-gray50);
	color: var(--color-gray950);
	@include useFont(tmds);
}

.footer {
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-top: 0.889rem;
	gap: 0.889rem;

	.btn {
		width: 100%;
		white-space: nowrap;
	}

	.btn-label {
		width: 100%;
		color: var(--color-brand500);

		span {
			@include useFont(tsmb);
		}
	}
}

@include screen768 {
	.cargo-detail {
		&__size {
			white-space: normal;
		}
	}
}

@include screen1440 {
	.section {
		top: 6.889rem;
		position: sticky;
	}
}

@include screen1920 {
	.section {
		padding: 1.778rem;
		border-radius: 1.333rem;
	}

	.title {
		@include useFont(dxss);
	}

	.cargo-detail {
		margin-top: 1.778rem;
		@include useFont(tmds);

		&__item {
			grid-template-columns: 5rem 1fr auto;
		}
	}

	.total {
		padding-top: 0.667rem;
		@include useFont(dxss);
	}

	.total-rub {
		@include useFont(tmds);
	}

	.upload-files {
		margin-top: 4rem;
	}

	.insurance {
		margin-top: 0.444rem;
		border-radius: 1.111rem;
		padding: 1.111rem;
		@include useFont(tlgs);
	}

	.footer {
		flex-direction: row;
		margin-top: 1.111rem;
		gap: 1.111rem;

		.btn {
			width: auto;
		}

		.btn-label {
			width: auto;
			white-space: pre-line;

			span {
				@include useFont(tlgb);
			}
		}
	}
}
</style>
